import * as React from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {
    AggregationSurveyReachOutcomes,
    AggregationSurveyReachSeries,
    SurveyReachBasis,
} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {NEGATIVE_COLOR, NEUTRAL_COLOR, POSITIVE_COLOR} from "../../../insights/kit/insightsColors";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {
    lowerIsBetterFor,
    SentimentChart,
    SentimentKey,
    SentimentLabels,
    SentimentSlice,
    SentimentSpec,
} from '../charts/SentimentChart';

interface ReachCardProps {
    series?: AggregationSurveyReachSeries;
    loading?: InsightsLoading;
}

type OutcomeField = 'submitted' | 'unfinished' | 'notStarted';

/*
 * The ring is Sentiment's. Answered in full takes its positive place, started and left its negative one - the share
 * the survey lost - and never started the grey: the guests who simply did not get to it. Each invitation sits in
 * exactly one slice, so the ring is the whole of what was sent.
 */
const OUTCOMES: Array<{key: SentimentKey; field: OutcomeField; color: string}> = [
    {key: 'positive', field: 'submitted', color: POSITIVE_COLOR},
    {key: 'negative', field: 'unfinished', color: NEGATIVE_COLOR},
    {key: 'neutral', field: 'notStarted', color: NEUTRAL_COLOR},
];

const totalOf = (outcomes: AggregationSurveyReachOutcomes) => outcomes.submitted + outcomes.unfinished + outcomes.notStarted;

const share = (value: number, base: number) => (base === 0 ? 0 : (value / base) * 100);

function toOutcomeSlices(
    data: AggregationSurveyReachOutcomes,
    compareData: AggregationSurveyReachOutcomes | undefined,
    labels: Pick<SentimentLabels, SentimentKey>
): SentimentSlice[] {
    const total = totalOf(data);
    const compareTotal = compareData == null ? 0 : totalOf(compareData);

    return OUTCOMES.map(({key, field, color}) => {
        const count = data[field];
        const compareCount = compareData?.[field] ?? null;

        const currentShare = share(count, total);
        const compareShare = compareCount == null ? null : share(compareCount, compareTotal);

        return {
            key,
            label: labels[key],
            color,
            count,
            share: currentShare,
            compareCount,
            compareShare,
            delta: compareShare == null ? null : currentShare - compareShare,
        };
    });
}

interface OutcomeTableProps {
    slices: SentimentSlice[];
    labels: SentimentLabels & {outcome: string};
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
    formatPoints: (value: number) => string;
}

function OutcomeTable({slices, labels, hasCompare, formatCount, formatPercent, formatPoints}: OutcomeTableProps) {
    const columns: Array<InsightsColumn<SentimentSlice> | false> = [
        {
            key: 'outcome',
            kind: 'label',
            label: labels.outcome,
            cell: slice => (
                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                    <Box sx={{width: 10, height: 10, borderRadius: '2px', bgcolor: slice.color, flexShrink: 0}}/>
                    <Box component={'span'}>{slice.label}</Box>
                </Stack>
            ),
        },
        {key: 'count', kind: 'value', label: labels.count, cell: slice => slice.count, format: formatCount},
        {key: 'share', kind: 'detail', label: labels.share, cell: slice => slice.share, format: formatPercent},
        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: slice => slice.compareCount, format: formatCount},
        hasCompare && {
            key: 'delta',
            kind: 'delta',
            label: labels.delta,
            cell: slice => slice.delta,
            format: formatPoints,
            lowerIsBetter: lowerIsBetterFor,
            color: slice => (slice.key === 'neutral' ? 'text.secondary' : undefined),
            comparison: true,
        },
    ];

    return <InsightsTable label={labels.outcome} columns={columns} rows={slices} rowKey={slice => slice.key}/>;
}

export function ReachCard({series, loading}: ReachCardProps) {
    const {t} = useTranslation();

    const outcomes = series?.current.outcomes;
    const compareOutcomes = series?.compare?.outcomes ?? undefined;
    const hasCompare = compareOutcomes != null;
    const visits = outcomes?.basis === SurveyReachBasis.VISITS;

    const {count: formatCount, percent: formatPercent, points: formatPoints} = useInsightsFormatters();

    const labels = React.useMemo<SentimentLabels>(
        () => ({
            positive: t('Submitted'),
            negative: t('Started, not finished'),
            neutral: t('Not started'),
            replies: t(visits ? 'visits' : 'invitations'),
            count: t('Count'),
            compare: t('Compare period'),
            share: t('Share'),
            delta: t('Change'),
        }),
        [t, visits],
    );

    const tableLabels = React.useMemo(() => ({outcome: t('Outcome'), ...labels}), [labels, t]);

    const slices = React.useMemo(
        () => (outcomes == null ? [] : toOutcomeSlices(outcomes, compareOutcomes, labels)),
        [compareOutcomes, labels, outcomes],
    );

    const total = outcomes == null ? 0 : totalOf(outcomes);

    // An anonymous survey invites nobody: its headline is the share of visits that finished.
    const value = visits
        ? (total === 0 ? null : share(outcomes!.submitted, total))
        : series?.summary.responseRate ?? null;
    const previous = visits
        ? (compareOutcomes == null || totalOf(compareOutcomes) === 0 ? null : share(compareOutcomes.submitted, totalOf(compareOutcomes)))
        : series?.summary.previousResponseRate ?? null;

    const headline = (
        <CardHeadline
            value={value == null ? '—' : formatPoints(value)}
            suffix={t(visits ? 'finished' : 'response rate')}
            change={previous == null ? null : {
                title: t(visits ? 'Finished' : 'Response rate'),
                current: value == null ? '—' : formatPoints(value),
                previous: formatPoints(previous),
                delta: value == null ? null : value - previous,
                formatDelta: formatPoints,
            }}
        />
    );

    // As on the NPS split, a compare period alone does not draw the rings: every share now would be 0 of 0.
    const empty = total === 0 ? (
        <Typography variant={'body2'} color={'text.secondary'}>
            {t(visits ? 'No responses in this period.' : 'Nobody was invited in this period.')}
        </Typography>
    ) : undefined;

    const spec: SentimentSpec = {slices, labels, formatCount, formatPercent, formatPoints};

    return (
        <SeriesCard
            title={t('Reach')}
            hint={t('What became of the invitations sent in this period, whenever the guest got to them: answered in full, started but left, or never started. The headline is the response rate, the share answered in full, the same figure as on the stat card. The outer ring is this period and the inner ring the one it is compared against; on an anonymous survey the ring splits its visits instead.')}
            headline={headline}
            loading={loading}
            empty={empty}
            chart={<SentimentChart {...spec}/>}
            table={(
                <OutcomeTable
                    slices={slices}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                    formatPoints={formatPoints}
                />
            )}
        />
    );
}
